// ============================================
// Variable Manager — replaces Unity VariableManager.cs
// Tracks base stats, buffs, flags, names
// Observes Ink variable changes via story observers
// ============================================

import { events, Events } from './events.js';
import { Stats, Flags } from './constants.js';

// Stats that have a base cap of 100 (buffs stack on top)
const CAPPED_STATS = new Set([
    Stats.FEMININITY,
    Stats.SUBMISSIVENESS,
    Stats.SHAME,
    Stats.AROUSAL,
]);

// Name-type stats (string values, not numeric)
const NAME_STATS = new Set([
    'PlayerName',
    'FeminineName',
    'MasterName',
    'GirlfriendName',
]);

// All observed Ink variable names (must match Ink VAR declarations exactly).
//
// Drift-class prevention rule (v0.2.5.1, foundby:iluan):
// Any var listed here gets an inkjs ObserveVariable callback that mirrors
// Ink mutations into baseVar/flags. Observers are paused during refreshView's
// _processStoryContent (so half-finished overnight transitions don't flicker
// the UI), so syncFromInk() is called at the END of every refreshView and at
// every load to repair the JS shadow against Ink. As long as a new flag/var
// is added here, that protection runs automatically — do NOT manually push to
// the JS shadow from gameplay code; let the observer + syncFromInk handle it.
//
// What NOT to add here: vars that are written ONLY from JS (phone-app state,
// pure UI flags) — they belong in inkState only, otherwise the JS shadow
// mirror creates the very drift this list is supposed to prevent.
const OBSERVED_VARIABLES = [
    Stats.MONEY,
    Stats.ENERGY,
    Stats.AROUSAL,
    Stats.FEMININITY,
    Stats.SUBMISSIVENESS,
    Stats.SHAME,
    'TimeSlot',
    'Day',
    'PlayerName',
    'FeminineName',
    'MasterName',
    'GirlfriendName',
    'Allure',
    'Comfort',
    // Flags
    Flags.HYPNO_UNLOCKED,
    Flags.IS_DIRTY,
    Flags.IS_DRUNK,
    Flags.IS_TIRED,
    Flags.HAS_MAKEUP,
    Flags.IS_VISIBLY_AROUSED,
    Flags.WEARING_CHASTITY,
    Flags.EQUIPPED_FEMININE,
    Flags.EQUIPPED_PANTIES,
    // G4 fix (audit 2026-05-24): 4 new equipped flags missing from observed
    // list, getFlag() returned false until next inventory event.
    Flags.EQUIPPED_CUFFS,
    Flags.EQUIPPED_HEELS,
    Flags.EQUIPPED_NIPPLE_CLAMPS,
    Flags.EQUIPPED_WAIST_TRAINER,
    Flags.WEARING_WIG,
    Flags.HAS_LONG_HAIR,
    Flags.WEARING_VIB,
    // Ink-computed variables
    'max_energy',
    'hrt_mood_swing',
    // Weekend Events (v0.2.5)
    'weekend_event_pending',
    'weekend_event_done_today',
    'weekend_event_attended',
    'weekend_event_last_id',
    'weekend_event_host_id',
    'weekend_pool_party_first',
    'weekend_foam_night_first',
    'weekend_beach_bonfire_first',
    'weekend_cosplay_first',
    'weekend_master_social_first',
    'weekend_stream_crossover_first',
    'weekend_loft_rooftop_first',
    'weekend_walk_of_shame_done',
    'master_social_deferred',
    // Streaming session state — read by streaming.js for header/tip-goal/
    // tip-animation. Without observation the UI never sees tip changes
    // and the chat flood was the only thing players saw, which faked
    // dollar amounts and made every stream feel like a flop at end.
    's_viewers',
    's_tips',
    's_round',
    's_max_rounds',
    's_hype',
    's_tip_goal',
    's_tip_goal_reached',
    's_category',
    's_collab_partner',
    'sissy_followers',
];

// Map from variable name to Stats enum key (for stat lookup)
const STAT_NAME_MAP = {};
for (const key of Object.keys(Stats)) {
    STAT_NAME_MAP[Stats[key]] = Stats[key];
}

// Map from variable name to Flags enum key.
//
// v0.2.7 (audit 2026-05-23): expanded to ALL boolean entries in
// OBSERVED_VARIABLES, not just the Flags enum. Pre-fix, observed booleans
// like `weekend_event_pending`, `master_social_deferred`, `s_tip_goal_reached`
// fell through to the generic VARIABLE_CHANGED branch and were never written
// to `this.flags`. UI code calling `variableManager.getFlag('weekend_event_pending')`
// silently returned false even when Ink had set it true.
const FLAG_NAME_MAP = {};
for (const key of Object.keys(Flags)) {
    FLAG_NAME_MAP[Flags[key]] = Flags[key];
}
// Expand: every OBSERVED_VARIABLE that's a known boolean shape (suffix _done,
// _pending, _deferred, _reached, EquippedX, wearing_X, has_X, is_X, _enabled,
// _unlocked) gets a passthrough entry. The observer callback writes the same
// key as both Ink-VAR-name and flags-Map-key. No naming asymmetry — JS
// `getFlag('x')` matches the Ink `VAR x`.
const _BOOL_SUFFIX_RE = /(_done|_pending|_deferred|_reached|_active|_enabled|_unlocked|_chose|_chosen|_attended|_today|_first|_offered|_seen|_set|_started|_complete|_completed|_open|_blocked|_ready|_disabled|_received|_sent|_introduced|_revealed|_returning|_locked|_mastered|_partial|_unlocked|_visible|_dressed|_owned)$/;
const _BOOL_PREFIX_RE = /^(Equipped|wearing_|has_|is_|owns_|set_|knows_|first_|sms_|hint_)/;
for (const varName of OBSERVED_VARIABLES) {
    if (FLAG_NAME_MAP[varName]) continue;
    if (_BOOL_SUFFIX_RE.test(varName) || _BOOL_PREFIX_RE.test(varName)) {
        FLAG_NAME_MAP[varName] = varName;
    }
}

class VariableManager {
    constructor() {
        /** @type {Map<string, number|string>} Base stat values (before buffs) */
        this.baseVar = new Map();

        /** @type {Map<string, number>} Equipment buff values per stat */
        this.buffedVar = new Map();

        /** @type {Map<string, boolean>} Flag values */
        this.flags = new Map();

        /** @type {object|null} inkjs Story reference */
        this._story = null;

        // F05: blockObservers as a depth counter, not a boolean.
        // Pre-fix: nested setBuffedVar/setValue/setFlag calls inside an outer
        // refreshView's blockObservers=true window had their own try/finally
        // that wrote `blockObservers = false`, mid-flight unblocking the outer
        // protection. With a counter, set(true) increments and set(false)
        // decrements; the OUTER block stays active until its OWN finally runs.
        // External API stays identical — `vm.blockObservers = true/false`
        // continues to work, internally routed through the counter.
        this._blockDepth = 0;

        this._createStartVariables();
    }

    /** Returns true if observer callbacks should be suppressed right now. */
    get blockObservers() { return this._blockDepth > 0; }

    /**
     * Setter — true increments depth, false decrements (clamped at 0).
     * Call sites that pair `=true` with `=false` in try/finally now nest
     * correctly. A stray `=false` without prior `=true` is a no-op.
     */
    set blockObservers(v) {
        if (v) {
            this._blockDepth++;
        } else {
            if (this._blockDepth > 0) this._blockDepth--;
        }
    }

    // ------------------------------------------------------------------
    // Initialization
    // ------------------------------------------------------------------

    /**
     * Set default stat/flag values for a new game.
     */
    _createStartVariables() {
        this.baseVar.set(Stats.MONEY, 200);
        this.baseVar.set(Stats.ENERGY, 100);
        this.baseVar.set(Stats.AROUSAL, 0);
        this.baseVar.set(Stats.FEMININITY, 0);
        this.baseVar.set(Stats.SUBMISSIVENESS, 0);
        this.baseVar.set(Stats.SHAME, 0);
        this.baseVar.set('TimeSlot', 0);
        this.baseVar.set('Day', 1);
        this.baseVar.set('Allure', 0);
        this.baseVar.set('Comfort', 0);

        // Names
        this.baseVar.set('PlayerName', 'Jamie');
        this.baseVar.set('MasterName', '');
        this.baseVar.set('GirlfriendName', 'Sarah');

        // Flags
        this.flags.set(Flags.EQUIPPED_PANTIES, false);
        this.flags.set(Flags.EQUIPPED_FEMININE, false);
        this.flags.set(Flags.HYPNO_UNLOCKED, false);
    }

    /**
     * Reset all state for a new game.
     */
    resetForNewGame() {
        this.baseVar.clear();
        this.buffedVar.clear();
        this.flags.clear();
        this._story = null;
        this._createStartVariables();
    }

    /**
     * Hook into an inkjs Story instance: register variable observers.
     * @param {object} story - inkjs Story instance
     */
    initHookStoryEvents(story) {
        this._story = story;

        // Register a single observer function for all tracked variables
        for (const varName of OBSERVED_VARIABLES) {
            try {
                story.ObserveVariable(varName, (name, newValue) => {
                    this._onInkVariableChanged(name, newValue);
                });
            } catch (e) {
                // Variable may not exist in compiled story — skip silently
                console.warn(`[VariableManager] Could not observe '${varName}': ${e.message}`);
            }
        }
    }

    // ------------------------------------------------------------------
    // Ink Observer Callback
    // ------------------------------------------------------------------

    /**
     * Called by inkjs when an observed variable changes in the story.
     * @param {string} varName
     * @param {*} newValue
     */
    _onInkVariableChanged(varName, newValue) {
        if (!this._story || this.blockObservers) return;

        // String variables (names)
        if (typeof newValue === 'string') {
            if (NAME_STATS.has(varName)) {
                this.baseVar.set(varName, newValue);
                events.emit(Events.NAME_CHANGED, varName, newValue);
            }
            return;
        }

        // Integer stat changes from Ink
        if (typeof newValue === 'number' && Number.isInteger(newValue)) {
            const statName = STAT_NAME_MAP[varName];

            if (statName) {
                const currentBuff = this.buffedVar.get(statName) || 0;
                let calculatedBase = Math.max(0, newValue - currentBuff);
                let inkTotal = newValue;

                // Cap base for growth-capped stats (Fem lifts to 200 post-capstone)
                const femBaseCapA = this._capFor(statName);
                if (CAPPED_STATS.has(statName) && calculatedBase > femBaseCapA) {
                    calculatedBase = femBaseCapA;
                    const correctedTotal = calculatedBase + currentBuff;
                    this.blockObservers = true;
                    try { this._story.variablesState[varName] = correctedTotal; }
                    finally { this.blockObservers = false; }
                    inkTotal = correctedTotal;
                }
                // Floor at 0: write back to Ink if stat went negative
                else if (inkTotal < 0) {
                    const correctedTotal = currentBuff; // base 0 + buff
                    this.blockObservers = true;
                    try { this._story.variablesState[varName] = correctedTotal; }
                    finally { this.blockObservers = false; }
                    inkTotal = correctedTotal;
                    calculatedBase = 0;
                }

                this.baseVar.set(statName, calculatedBase);
                events.emit(Events.STAT_CHANGED, varName, inkTotal);
            }
            else if (varName === 'max_energy') {
                // Ink-computed variable — no base/buff tracking, just notify
                events.emit(Events.STAT_CHANGED, varName, newValue);
            }
            else if (varName === 'TimeSlot' || varName === 'Day') {
                this.baseVar.set(varName, newValue);
                events.emit(Events.STAT_CHANGED, varName, newValue);
                events.emit(Events.VARIABLE_CHANGED, varName, newValue);
            }
            else {
                // Generic non-stat integer var (streaming s_tips/s_viewers,
                // hrt_mood_swing, weekend_event_*, future flags). UI listeners
                // (streaming.js, weekend events, phone apps) react via
                // VARIABLE_CHANGED — fire it for everything the observer list
                // tracked, so adding a var to OBSERVED_VARIABLES is enough on
                // its own without a matching switch case.
                events.emit(Events.VARIABLE_CHANGED, varName, newValue);
            }
            return;
        }

        // Boolean flags
        if (typeof newValue === 'boolean') {
            const flagName = FLAG_NAME_MAP[varName];
            if (flagName) {
                this.flags.set(flagName, newValue);
                events.emit(Events.FLAG_CHANGED, varName, newValue);
            } else {
                // F07: VAR is in OBSERVED_VARIABLES but not in Flags enum
                // (e.g. weekend_event_done_today, weekend_pool_party_first,
                // s_tip_goal_reached). Fire generic VARIABLE_CHANGED so any
                // listener can react, and warn in dev mode so missing
                // FLAG_NAME_MAP entries surface.
                events.emit(Events.VARIABLE_CHANGED, varName, newValue);
                if (typeof window !== 'undefined' &&
                    window.localStorage?.getItem?.('PnO_Debug') === 'true') {
                    console.warn(`[VariableManager] '${varName}' is bool in Ink but not in FLAG_NAME_MAP — using generic emit`);
                }
            }
            return;
        }
    }

    // ------------------------------------------------------------------
    // Push/Sync (startup and load ONLY)
    // ------------------------------------------------------------------

    /**
     * Push all tracked C# variables to Ink. Call ONLY on startup or after loading a save.
     * CRITICAL: Never call after story.variablesState[key] = value (observers auto-sync).
     * @param {object} story - inkjs Story instance
     */
    pushVariablesToInk(story) {
        // F28: warn when called mid-continue. Observer bridge auto-syncs Ink
        // writes, so redundant pushes during a story cycle fire STAT_CHANGED
        // on every tracked var and cause visible UI flicker. Dev-mode only.
        if (typeof window !== 'undefined' && window._sm && window._sm._inContinueCycle) {
            const dbg = window.localStorage?.getItem?.('PnO_Debug') === 'true';
            if (dbg) {
                console.warn('[variables] pushVariablesToInk() called during story-continue cycle — '
                    + 'this fires STAT_CHANGED for every observed var. Use direct story.variablesState writes; '
                    + 'observers will auto-sync. Stack:\n' + (new Error()).stack);
            }
        }
        this._story = story;
        this.blockObservers = true;

        // Build a Set for O(1) lookup. baseVar may contain stray non-observed
        // entries from legacy phone.js setVar (which used to write to baseVar
        // for any var name). Pushing those back to Ink overwrites Ink-managed
        // state — e.g. dare_active stuck at 10 even after Ink reset it to 0
        // on dare completion.
        const pushable = new Set(OBSERVED_VARIABLES);

        try {
            // Push stats (base + buffs).
            // Skip Allure / Comfort — equipment-only stats with no base.
            // inventoryManager.recalculateAllBuffs → setBuffedVar pushes the
            // pure buff value to Ink (since baseVar must stay 0). Including
            // them here would re-push baseVar+buff and either double-count or
            // resurrect a corrupted negative base from an old save.
            //
            // Skip max_energy — it's an Ink-COMPUTED var (get_max_energy() =
            // 100 + max_energy_base_bonus + bed/decor/mastery/wellness bonuses),
            // not a base+buff stat. The observer (line ~294) and syncFromInk
            // both intentionally leave baseVar.max_energy untouched. The editor
            // seeds baseVar.max_energy via setValue for max-energy backgrounds/
            // perks; once the bed/decor upgrade recomputes max_energy in Ink,
            // that baseVar entry is stale. Pushing it here clobbers the correct
            // Ink value on reload (foundby:曉明 v0.2.7.3 — max-energy background
            // + bed upgrade lost the bed bonus after save reload). Reads always
            // source from story.variablesState['max_energy'] directly, so no
            // consumer needs the baseVar mirror.
            for (const [key, baseValue] of this.baseVar) {
                if (!pushable.has(key)) continue;
                if (key === 'Allure' || key === 'Comfort' || key === 'max_energy') continue;
                const buff = this.buffedVar.get(key) || 0;
                const totalValue = (typeof baseValue === 'number') ? baseValue + buff : baseValue;
                try {
                    story.variablesState[key] = totalValue;
                } catch (e) {
                    // Variable may not exist in Ink
                }
                this._triggerObserver(key, totalValue);
            }

            // Push flags as int for Ink compatibility (Ink stores bools as 0/1).
            // Skip non-observed flags — defense-in-depth for legacy saves that
            // carry stray entries from pre-v0.2.7 inventory pusher / shop
            // setsOnPurchase (which wrote arbitrary names into this.flags).
            // Pushing those back to Ink overwrites Ink-managed state on reload.
            // Mirrors the same filter pattern as the baseVar loop above.
            for (const [flagName, value] of this.flags) {
                if (!pushable.has(flagName)) continue;
                try {
                    // Ink stores booleans as integers (0/1)
                    story.variablesState[flagName] = value ? 1 : 0;
                } catch (e) {
                    try { story.variablesState[flagName] = !!value; } catch (e2) { /* skip */ }
                }
            }
        } finally {
            this.blockObservers = false;
        }

        // Emit bulk sync event so UI can refresh all bars from current Ink state
        events.emit(Events.VARIABLE_CHANGED, '_bulkSyncDone', true);
    }

    /**
     * Pull every OBSERVED_VARIABLE from Ink back into the JS shadow state
     * (baseVar + flags). Called at the end of refreshView to recover from the
     * blockObservers=true window during which Ink mutations don't fire the
     * observer callbacks. Without this sync, flags like is_dirty cleared by
     * Ink overnight processing stayed `true` in the JS map, and the next save
     * captured the stale value; on reload pushVariablesToInk pushed it back
     * to Ink, resurrecting the flag.
     */
    syncFromInk() {
        if (!this._story) return;
        for (const varName of OBSERVED_VARIABLES) {
            try {
                const inkValue = this._story.variablesState[varName];
                if (typeof inkValue === 'boolean') {
                    const flagName = FLAG_NAME_MAP[varName];
                    if (flagName) this.flags.set(flagName, inkValue);
                } else if (typeof inkValue === 'number' && Number.isInteger(inkValue)) {
                    const statName = STAT_NAME_MAP[varName];
                    if (statName) {
                        const currentBuff = this.buffedVar.get(statName) || 0;
                        let calculatedBase = Math.max(0, inkValue - currentBuff);
                        const femBaseCapB = this._capFor(statName);
                        if (CAPPED_STATS.has(statName) && calculatedBase > femBaseCapB) {
                            calculatedBase = femBaseCapB;
                            const correctedTotal = calculatedBase + currentBuff;
                            this.blockObservers = true;
                            try { this._story.variablesState[varName] = correctedTotal; }
                            finally { this.blockObservers = false; }
                        }
                        this.baseVar.set(statName, calculatedBase);
                    } else if (varName === 'TimeSlot' || varName === 'Day') {
                        this.baseVar.set(varName, inkValue);
                    }
                    // Allure / Comfort: equipment-only stats, baseVar must stay
                    // 0. inventoryManager.setBuffedVar pushes the pure buff to
                    // Ink. Mirroring the Ink total back into baseVar here would
                    // re-add the buff every push → unbounded drift.
                } else if (typeof inkValue === 'string' && NAME_STATS.has(varName)) {
                    this.baseVar.set(varName, inkValue);
                }
            } catch (e) { /* var may not exist in compiled story */ }
        }
    }

    /**
     * Fire observer events without going through Ink. Used during pushVariablesToInk.
     * Emits STAT_CHANGED for UI bar updates but marks as bulk sync so notifications
     * are suppressed (no phantom +Energy/-Energy popups).
     * @param {string} varName
     * @param {*} newValue
     */
    _triggerObserver(varName, newValue) {
        if (typeof newValue === 'string') {
            events.emit(Events.NAME_CHANGED, varName, newValue);
        }
        // Numeric STAT_CHANGED NOT emitted — pushVariablesToInk emits BULK_SYNC_DONE
        // at the end, and the caller is responsible for UI sync via statsUI.setAll().
    }

    // ------------------------------------------------------------------
    // Getters
    // ------------------------------------------------------------------

    /**
     * Get the base value of a stat (before buffs).
     * Masculinity is computed as 100 - Femininity.
     * @param {string} stat - Stats constant
     * @returns {number|string}
     */
    getValue(stat) {
        if (stat === Stats.MASCULINITY) {
            const fem = this.baseVar.get(Stats.FEMININITY) || 0;
            return 100 - fem;
        }
        return this.baseVar.has(stat) ? this.baseVar.get(stat) : 0;
    }

    /**
     * Get base + equipment buff total (what Ink sees). Use for requirement checks.
     * @param {string} stat - Stats constant
     * @returns {number}
     */
    getTotalValue(stat) {
        if (stat === Stats.MASCULINITY) {
            const femBase = this.baseVar.get(Stats.FEMININITY) || 0;
            const femBuff = this.buffedVar.get(Stats.FEMININITY) || 0;
            return 100 - (femBase + femBuff);
        }
        const base = (typeof this.baseVar.get(stat) === 'number') ? this.baseVar.get(stat) : 0;
        const buff = this.buffedVar.get(stat) || 0;
        return base + buff;
    }

    /**
     * Get a flag value.
     * @param {string} flagName - Flags constant
     * @returns {boolean}
     */
    getFlag(flagName) {
        return this.flags.get(flagName) || false;
    }

    // ------------------------------------------------------------------
    // Setters
    // ------------------------------------------------------------------

    /**
     * Set a base stat value and push to Ink.
     * @param {string} stat - Stats constant
     * @param {number|string} value
     */
    setValue(stat, value) {
        this.baseVar.set(stat, value);
        if (this._story) {
            const buff = this.buffedVar.get(stat) || 0;
            const totalValue = (typeof value === 'number') ? value + buff : value;
            this.blockObservers = true;
            try { this._story.variablesState[stat] = totalValue; }
            finally { this.blockObservers = false; }
            // Emit STAT_CHANGED so UI (sidebar, shop) updates immediately
            if (typeof totalValue === 'number') {
                events.emit(Events.STAT_CHANGED, stat, totalValue);
            } else {
                this._triggerObserver(stat, totalValue);
            }
        }
    }

    /**
     * E-08 v0.2.5: safe wrapper around story.EvaluateFunction.
     * Logs warnings on failure instead of silently swallowing them.
     * Per memory feedback_evaluatefunction_quirks.md: args MUST be array
     * (scalar silently → undefined), and lookAhead-unsafe externals are
     * skipped by inkjs. This wrapper enforces array args + warns on missing
     * function so renamed/removed Ink helpers don't lose stat effects in
     * silence.
     * @param {string} fn - Ink function name (e.g. 'fem_gain', 'update_max_energy')
     * @param {Array} [args=[]] - arguments array (NOT a scalar)
     * @returns {*} the function's return value, or undefined on error
     */
    evalSafe(fn, args = []) {
        if (!this._story) {
            console.warn(`[VariableManager.evalSafe] no story for '${fn}'`);
            return undefined;
        }
        if (!Array.isArray(args)) {
            console.warn(`[VariableManager.evalSafe] '${fn}' args must be array, got ${typeof args} — wrapping`);
            args = [args];
        }
        try {
            return this._story.EvaluateFunction(fn, args);
        } catch (e) {
            console.warn(`[VariableManager.evalSafe] '${fn}' failed:`, e?.message || e);
            return undefined;
        }
    }

    /**
     * Set an equipment buff value and push total to Ink.
     * @param {string} stat - Stats constant
     * @param {number} value - Buff amount
     */
    setBuffedVar(stat, value) {
        const oldBuff = this.buffedVar.get(stat) || 0;
        this.buffedVar.set(stat, value);

        if (this._story) {
            // Adjust the buff DELTA against the LIVE Ink value, not the JS
            // baseVar. baseVar goes stale during _processStoryContent's
            // blockObservers window (Ink mutations of observed vars aren't
            // mirrored back until syncFromInk), so the old `base + buff` push
            // re-wrote that stale base over an in-knot do_action()/fem_gain()
            // change. A knot that costs energy / grants Fem and THEN fires
            // #item:recalc_buffs (the 4 comfort-training knots) therefore
            // consumed NO energy and granted NO Fem — recalc pushed the stale
            // pre-action base back. foundby:Anne-Sophie 0.2.7.5 (heels / corset
            // training "don't consume energy"; same path overflowed the bar to
            // 145/140 when a stale base outran the live value).
            //   new total = liveInk - oldBuff + newBuff
            // When baseVar IS fresh (normal equip), liveInk == base + oldBuff,
            // so this reduces to base + newBuff — identical to the old formula.
            let totalValue;
            const liveInk = this._story.variablesState[stat];
            if (typeof liveInk === 'number') {
                totalValue = liveInk - oldBuff + value;
            } else {
                const base = (typeof this.baseVar.get(stat) === 'number') ? this.baseVar.get(stat) : 0;
                totalValue = base + value;
            }
            // Block observers so the Ink observer doesn't misinterpret
            // the buff-adjusted total as a base stat change
            this.blockObservers = true;
            try { this._story.variablesState[stat] = totalValue; }
            finally { this.blockObservers = false; }
            // Still notify UI so stat bars update
            this._triggerObserver(stat, totalValue);
        }
    }

    /**
     * Set a flag value and push to Ink.
     * @param {string} flagName - Flags constant
     * @param {boolean} value
     */
    setFlag(flagName, value) {
        const boolValue = !!value;
        this.flags.set(flagName, boolValue);
        if (this._story) {
            // Ink stores booleans as integers (0/1), not JS booleans
            // Some Ink VARs are declared as integers, some as booleans
            // Always push as integer to be safe
            const inkValue = boolValue ? 1 : 0;
            this.blockObservers = true;
            try { this._story.variablesState[flagName] = inkValue; }
            catch (e) {
                // Variable might not exist in Ink — try as boolean
                try { this._story.variablesState[flagName] = boolValue; }
                catch (e2) { /* skip */ }
            }
            finally { this.blockObservers = false; }
        }
        events.emit(Events.FLAG_CHANGED, flagName, boolValue);
    }

    /**
     * Set character names and push to Ink.
     * @param {string} playerName
     * @param {string} masterName
     * @param {string} girlfriendName
     */
    setCharacterNames(playerName, masterName, girlfriendName, feminineName) {
        this.baseVar.set('PlayerName', playerName);
        this.baseVar.set('MasterName', masterName);
        this.baseVar.set('GirlfriendName', girlfriendName);
        // feminineName optional (added 0.2.9) — undefined keeps the existing value
        // so legacy 3-arg callers don't wipe it. Empty string is a valid choice.
        if (feminineName !== undefined) this.baseVar.set('FeminineName', feminineName);

        if (this._story) {
            this.blockObservers = true;
            try {
                this._story.variablesState['PlayerName'] = playerName;
                this._story.variablesState['MasterName'] = masterName;
                this._story.variablesState['GirlfriendName'] = girlfriendName;
                if (feminineName !== undefined) this._story.variablesState['FeminineName'] = feminineName;
            } finally { this.blockObservers = false; }
        }
    }

    /**
     * Clamp base stats at 100. Call after loading a save.
     */
    /**
     * Base cap for a growth-capped stat. Femininity lifts to 200 once the
     * Transformation-Ladder capstone (fully_transformed) opens the pink
     * deepening zone; every other capped stat stays 100. Mirrors Ink fem_cap().
     */
    _capFor(statName) {
        if (statName === Stats.FEMININITY) {
            try {
                if (this._story && this._story.variablesState['fully_transformed']) return 200;
            } catch (e) { /* default 100 */ }
        }
        return 100;
    }

    clampBaseStats() {
        for (const stat of CAPPED_STATS) {
            const val = this.baseVar.get(stat);
            const cap = this._capFor(stat);
            if (typeof val === 'number' && val > cap) {
                this.baseVar.set(stat, cap);
            }
        }
    }

    /**
     * Check if the player has a given personality trait.
     * @param {string} traitId
     * @returns {boolean}
     */
    hasTrait(traitId) {
        if (!this._story || !traitId) return false;
        try {
            const t1 = this._story.variablesState['trait_1'] || '';
            const t2 = this._story.variablesState['trait_2'] || '';
            return t1 === traitId || t2 === traitId;
        } catch(e) { return false; }
    }

    /**
     * Read a raw Ink variable value.
     * @param {string} varName
     * @returns {*}
     */
    getInkVariable(varName) {
        if (!this._story) return undefined;
        try { return this._story.variablesState[varName]; }
        catch(e) { return undefined; }
    }

    // ------------------------------------------------------------------
    // Serialization (for save/load)
    // ------------------------------------------------------------------

    /**
     * Export all tracked state for saving.
     * @returns {object}
     */
    exportState() {
        return {
            baseVar: Object.fromEntries(this.baseVar),
            buffedVar: Object.fromEntries(this.buffedVar),
            flags: Object.fromEntries(this.flags),
        };
    }

    /**
     * Import saved state.
     * @param {object} state - From exportState()
     */
    importState(state) {
        if (!state) return;

        if (state.baseVar) {
            this.baseVar.clear();
            for (const [k, v] of Object.entries(state.baseVar)) {
                this.baseVar.set(k, v);
            }
        }
        if (state.buffedVar) {
            this.buffedVar.clear();
            for (const [k, v] of Object.entries(state.buffedVar)) {
                this.buffedVar.set(k, v);
            }
        }
        if (state.flags) {
            this.flags.clear();
            for (const [k, v] of Object.entries(state.flags)) {
                this.flags.set(k, v);
            }
        }
    }
}

/** @type {VariableManager} Singleton */
export const variableManager = new VariableManager();
